import { Graph, layoutGraph } from '../model';
import { Port, portsOfNode, targetOf, TRIGGER } from './ports';

/** Geometry of the editor canvas (docs/pilot/SPEC.md §12.5): node boxes, port anchors, edge curves, view fitting. Pure. */

export type Pos = { x: number; y: number };
export type Layout = Record<string, Pos>;
export interface View {
  x: number;
  y: number;
  k: number;
}

export const GRID = 8;
export const NODE_W = 232;
export const NODE_H = 96;
export const TRIGGER_H = 72;
/** Room for the port handles under a node. */
export const PORT_GAP = 28;
export const MIN_K = 0.25;
export const MAX_K = 2;
const PORT_SPACING = 52;

export const snap = (v: number) => Math.round(v / GRID) * GRID;
export const clampK = (k: number) => Math.min(MAX_K, Math.max(MIN_K, k));

/** Wider nodes for branches/splits with many exits, so every handle keeps a 44 px touch target. */
export function nodeSize(graph: Graph, id: string): { w: number; h: number } {
  if (id === TRIGGER) return { w: NODE_W, h: TRIGGER_H };
  const ports = portsOfNode(graph, id).length;
  return { w: Math.max(NODE_W, ports * PORT_SPACING + 24), h: NODE_H };
}

/** Automatic top-down layout (the read-only canvas's), trigger included. */
export function autoLayout(graph: Graph): Layout {
  const { nodes, triggerX } = layoutGraph({ ...graph, layout: undefined });
  const out: Layout = { [TRIGGER]: { x: snap(triggerX), y: 16 } };
  for (const n of nodes) out[n.id] = { x: snap(n.x), y: snap(n.y) };
  return out;
}

/** Positions for every node: stored ones first, the automatic ones for the rest. */
export function completeLayout(graph: Graph, layout: Layout | null | undefined): Layout {
  const ids = [TRIGGER, ...Object.keys(graph.steps)];
  if (layout && ids.every((id) => layout[id])) return pruneLayout(graph, layout);
  const auto = autoLayout(graph);
  const out: Layout = {};
  for (const id of ids) out[id] = layout?.[id] ?? auto[id];
  return out;
}

/** Drops positions of steps that are gone. */
export function pruneLayout(graph: Graph, layout: Layout): Layout {
  const out: Layout = {};
  for (const id of [TRIGGER, ...Object.keys(graph.steps)]) if (layout[id]) out[id] = layout[id];
  return out;
}

/** Position for a new step: under the node its first incoming port belongs to, shifted right while the spot is busy. */
export function placeNew(graph: Graph, layout: Layout, id: string, near?: Pos): Pos {
  let p: Pos;
  if (near) p = { x: snap(near.x), y: snap(near.y) };
  else {
    const from = [TRIGGER, ...Object.keys(graph.steps)].find((src) => src !== id && portsOfNode(graph, src).some((port) => targetOf(graph, port) === id));
    const src = from ? layout[from] : null;
    if (src) {
      const ports = portsOfNode(graph, from!);
      const i = Math.max(0, ports.findIndex((port) => targetOf(graph, port) === id));
      const w = nodeSize(graph, from!).w;
      p = { x: snap(src.x + (ports.length > 1 ? (i - (ports.length - 1) / 2) * (NODE_W + 32) : 0) + (w - NODE_W) / 2), y: snap(src.y + (from === TRIGGER ? TRIGGER_H : NODE_H) + 96) };
    } else {
      const b = bounds(graph, layout);
      p = { x: snap(b.minX), y: snap(b.maxY + 72) };
    }
  }
  const busy = (q: Pos) => Object.entries(layout).some(([k, o]) => k !== id && Math.abs(o.x - q.x) < NODE_W - 16 && Math.abs(o.y - q.y) < NODE_H - 16);
  for (let i = 0; i < 12 && busy(p); i++) p = { x: p.x + NODE_W + 32, y: p.y };
  return p;
}

/** Shifts the nodes below an inserted node down so the insertion does not overlap (one row). */
export function makeRoom(graph: Graph, layout: Layout, id: string): Layout {
  const p = layout[id];
  if (!p) return layout;
  const out = { ...layout };
  for (const [k, q] of Object.entries(layout)) {
    if (k === id || k === TRIGGER) continue;
    if (q.y >= p.y - NODE_H / 2 && Math.abs(q.x - p.x) < NODE_W * 1.5) out[k] = { x: q.x, y: q.y + NODE_H + 72 };
  }
  void graph;
  return out;
}

export function bounds(graph: Graph, layout: Layout): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [id, p] of Object.entries(layout)) {
    if (id !== TRIGGER && !graph.steps[id]) continue;
    const { w, h } = nodeSize(graph, id);
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x + w);
    maxY = Math.max(maxY, p.y + h + PORT_GAP);
  }
  if (!Number.isFinite(minX)) return { minX: 0, minY: 0, maxX: NODE_W, maxY: NODE_H };
  return { minX, minY, maxX, maxY };
}

/** The view that shows the whole graph in a frame (never zoomed in beyond 100 %). */
export function fitView(graph: Graph, layout: Layout, frame: { w: number; h: number }, pad = 32): View {
  const b = bounds(graph, layout);
  const w = b.maxX - b.minX;
  const h = b.maxY - b.minY;
  const k = clampK(Math.min(1, (frame.w - pad * 2) / Math.max(w, 1), (frame.h - pad * 2) / Math.max(h, 1)));
  return { k, x: (frame.w - w * k) / 2 - b.minX * k, y: Math.max(pad, (frame.h - h * k) / 2) - b.minY * k };
}

/** Zoom to `k` keeping the screen point (sx, sy) over the same world point. */
export function zoomAt(v: View, k: number, sx: number, sy: number): View {
  const nk = clampK(k);
  const wx = (sx - v.x) / v.k;
  const wy = (sy - v.y) / v.k;
  return { k: nk, x: sx - wx * nk, y: sy - wy * nk };
}

export const toWorld = (v: View, sx: number, sy: number): Pos => ({ x: (sx - v.x) / v.k, y: (sy - v.y) / v.k });

/** Where a port's handle sits (bottom edge of its node, evenly spaced). */
export function portAnchor(graph: Graph, layout: Layout, port: Port): Pos {
  const p = layout[port.step] ?? { x: 0, y: 0 };
  const ports = portsOfNode(graph, port.step);
  const i = Math.max(0, ports.findIndex((q) => q.key === port.key));
  const { w, h } = nodeSize(graph, port.step);
  return { x: p.x + (w * (i + 1)) / (ports.length + 1), y: p.y + h };
}

/** Top-centre of a node: where edges arrive. */
export function inAnchor(graph: Graph, layout: Layout, id: string): Pos {
  const p = layout[id] ?? { x: 0, y: 0 };
  return { x: p.x + nodeSize(graph, id).w / 2, y: p.y };
}

/** A smooth curve from a port down to a node (also when the target sits above: the curve loops around). */
export function edgePath(a: Pos, b: Pos): string {
  const dy = Math.max(48, Math.abs(b.y - a.y) / 2);
  return `M ${a.x} ${a.y} C ${a.x} ${a.y + dy}, ${b.x} ${b.y - dy}, ${b.x} ${b.y}`;
}

function bezierPoint(a: Pos, b: Pos, t: number): Pos {
  const dy = Math.max(48, Math.abs(b.y - a.y) / 2);
  const c1 = { x: a.x, y: a.y + dy };
  const c2 = { x: b.x, y: b.y - dy };
  const u = 1 - t;
  return {
    x: u * u * u * a.x + 3 * u * u * t * c1.x + 3 * u * t * t * c2.x + t * t * t * b.x,
    y: u * u * u * a.y + 3 * u * u * t * c1.y + 3 * u * t * t * c2.y + t * t * t * b.y,
  };
}

export const edgeMid = (a: Pos, b: Pos) => bezierPoint(a, b, 0.5);

/** Distance from a point to an edge curve (sampled; good enough for hover and drop targets). */
export function distanceToEdge(a: Pos, b: Pos, p: Pos): number {
  let best = Infinity;
  for (let i = 0; i <= 24; i++) {
    const q = bezierPoint(a, b, i / 24);
    best = Math.min(best, Math.hypot(q.x - p.x, q.y - p.y));
  }
  return best;
}

/** The node under a world point (the trigger is never a drop target for a connection). */
export function nodeAt(graph: Graph, layout: Layout, p: Pos, opts: { withTrigger?: boolean } = {}): string | null {
  const ids = Object.keys(graph.steps).reverse();
  if (opts.withTrigger) ids.push(TRIGGER);
  for (const id of ids) {
    const q = layout[id];
    if (!q) continue;
    const { w, h } = nodeSize(graph, id);
    if (p.x >= q.x && p.x <= q.x + w && p.y >= q.y && p.y <= q.y + h) return id;
  }
  return null;
}
